import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFileUpload } from "@/dentlab/src/hooks/useFileUpload";
import { cn } from "@/dentlab/src/lib/util";
import { Button } from "@/dentlab/src/components/ui/button";

interface CustomFormFileUploadFieldProps {
	value: any;
	onChange: (value: any) => void;
	required?: boolean;
}

interface FileInfo {
	name: string;
	type: string;
	size: number;
	data: string | ArrayBuffer | null;
}

export const CustomFormFileUploadField: React.FC<
	CustomFormFileUploadFieldProps
> = ({ value, onChange, required }) => {
	const [files, setFiles] = useState<FileInfo[]>(value || []);
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const processFiles = async (fileList: File[]) => {
		const newFiles: FileInfo[] = [];

		for (const file of fileList) {
			const reader = new FileReader();
			const fileData = await new Promise<string | ArrayBuffer | null>((resolve) => {
				reader.onload = (e) => {
					resolve(e.target?.result || null);
				};
				reader.readAsDataURL(file);
			});

			if (fileData) {
				newFiles.push({
					name: file.name,
					type: file.type,
					size: file.size,
					data: fileData,
				});
			}
		}

		const updatedFiles = [...files, ...newFiles];
		setFiles(updatedFiles);
		onChange(updatedFiles);
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files || event.target.files.length === 0) return;

		const fileList = Array.from(event.target.files);
		await processFiles(fileList);

		// Clear the file input so the same file can be selected again if needed
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleFileUpload = async (droppedFiles: File[]) => {
		await processFiles(droppedFiles);
	};

	const { isDragging, isUploading, dragHandlers } = useFileUpload({
		onFileUpload: handleFileUpload,
	});

	const handleRemoveFile = (index: number) => {
		const updatedFiles = [...files];
		updatedFiles.splice(index, 1);
		setFiles(updatedFiles);
		onChange(updatedFiles);
	};

	return (
		<div className="w-full">
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
				className="hidden"
				multiple
				required={required && files.length === 0}
			/>

			<div
				{...dragHandlers}
				className={cn(
					"border-2 border-dashed rounded-md p-4 transition-colors",
					isDragging ? "border-blue-400 bg-blue-50" : "border-gray-300"
				)}
			>
				{isDragging ? (
					<div className="flex items-center justify-center min-h-[60px]">
						<Typography variant="body2" className="text-blue-600 font-medium">
							Dateien hier ablegen
						</Typography>
					</div>
				) : isUploading ? (
					<div className="flex items-center justify-center min-h-[60px]">
						<Typography variant="body2" className="text-gray-600">
							Dateien werden verarbeitet...
						</Typography>
					</div>
				) : (
					<>
						<div className="flex flex-col items-center justify-center min-h-[60px] gap-2">
							<Button
								variant="outline"
								onClick={() => fileInputRef.current?.click()}
								type="button"
							>
								<CloudUploadIcon className="mr-2 h-4 w-4" />
								Datei auswählen
							</Button>
							<Typography variant="caption" className="text-gray-500">
								oder Dateien hier ablegen
							</Typography>
						</div>
					</>
				)}
			</div>

			{files.length > 0 && (
				<div className="mt-3 border rounded-md p-2">
					<Typography variant="subtitle2" className="mb-2">
						Hochgeladene Dateien:
					</Typography>
					<div>
						{files.map((file, index) => (
							<div
								key={index}
								className="flex items-center justify-between p-2 border-b last:border-0"
							>
								<div className="flex items-center">
									<AttachFileIcon className="text-gray-500 mr-2" />
									<div>
										<Typography variant="body2">
											{file.name}
										</Typography>
										<Typography
											variant="caption"
											className="text-gray-500"
										>
											{(file.size / 1024).toFixed(1)} KB
										</Typography>
									</div>
								</div>
								<IconButton
									size="small"
									onClick={() => handleRemoveFile(index)}
									aria-label="Datei löschen"
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
